import { useEffect, useRef, useState } from 'react';
import { Calendar, Check, Download, FileText, MapPin, Pause, Play, X } from 'lucide-react';
import type { GroupSummary } from '../../../lib/group-chat';
import { participantLabel, personById } from '../../../lib/group-chat';
import {
  bubbleKind,
  contactVcf,
  eventIcs,
  fileExtension,
  formatBytes,
  formatDuration,
  mapsUrl,
  myRsvp,
  nextPollChoice,
  nextRsvp,
  nextVoiceSpeed,
  pollTally,
  rsvpCounts,
  stickerEmoji,
  thumbnailBox,
  canCancelUpload,
  myPollChoice,
  type GroupContent,
  type GroupEventRsvp,
  type GroupPollVote,
  type GroupRsvp,
  type UploadState,
} from '../../../lib/group-content';
import { StickerArt } from './StickerArt';
import { cancelGroupUpload, mediaLinkNow, respondToGroupEvent, useMediaLink, voteInGroupPoll } from '../../../lib/group-content-api';
import type { Message } from '../../../lib/use-chat';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { Modal } from '../../Modal';
import { Avatar, Button, Card, IconButton, ProgressBar, ProgressRing, SelectionIndicator, TextButton } from '../ui';
import { avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import { LinkifiedText } from '../../LinkifiedText';

/**
 * Group chat Phase 10c — the bubble body of a typed message: a photo, GIF or video (thumbnail, full-screen viewer), a
 * document card, a voice message player, a poll with live results, an event with RSVP, a place, a contact card, a
 * sticker; and a pending one's progress, cancel and retry. Every decision is `lib/group-content.ts`; iOS
 * `GroupContentViews` / Android `GroupContentViews` draw the same.
 */

const BI = { page_name: 'chat_home' } as const;

export interface GroupContentBodyProps {
  message: Message;
  content: GroupContent;
  conversationId: string | null;
  summary: GroupSummary | null;
  viewerId: string | undefined;
  authorName: string;
}

function download(name: string, mime: string, body: string) {
  const url = URL.createObjectURL(new Blob([body], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

const card: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  minWidth: 220,
  maxWidth: 300,
};

export function GroupContentBody(props: GroupContentBodyProps) {
  const { content, message } = props;
  const upload = message.groupPost?.upload;
  const kind = bubbleKind(content);
  const body = (() => {
    switch (kind) {
      case 'image':
      case 'gif':
      case 'video':
        return <MediaThumb {...props} localUrl={upload?.localUrl} pending={!!upload} />;
      case 'document':
        return <DocumentCard {...props} pending={!!upload} />;
      case 'voice':
        return <VoiceNote {...props} localUrl={upload?.localUrl} pending={!!upload} />;
      case 'poll':
        return <PollCard {...props} />;
      case 'event':
        return <EventCard {...props} />;
      case 'location':
        return <LocationCard {...props} />;
      case 'contact':
        return <ContactCard {...props} />;
      case 'sticker':
        return <StickerBody {...props} />;
    }
  })();
  return (
    <div data-testid={`group-content-${kind}`} style={{ position: 'relative' }}>
      {body}
      {upload && message.conversationId && <UploadOverlay conversationId={message.conversationId} clientMessageId={message.id ?? ''} state={upload.state} />}
      {content.caption && (
        <span dir="auto" style={{ whiteSpace: 'pre-wrap', display: 'block', marginTop: 8 }}>
          <LinkifiedText text={content.caption} />
        </span>
      )}
    </div>
  );
}

function UploadOverlay({ conversationId, clientMessageId, state }: { conversationId: string; clientMessageId: string; state: UploadState }) {
  const { t } = useTranslation();
  if (state.phase === 'failed' || state.phase === 'sent') return null;
  const percent = Math.round(state.progress * 100);
  return (
    <div data-testid="group-upload-progress" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12, color: 'var(--ds-text-secondary)' }}>
      <ProgressRing percent={state.phase === 'posting' ? 100 : percent} arcColor="var(--accent-solid)" size={20} stroke={3} />
      <span aria-live="polite">{t('chat.group.upload.progress', { percent: state.phase === 'posting' ? 100 : percent })}</span>
      {canCancelUpload(state) && (
        <IconButton size="small" aria-label={t('chat.group.upload.cancel')} title={t('chat.group.upload.cancel')} onClick={() => cancelGroupUpload(conversationId, clientMessageId)}>
          <X size={14} strokeWidth={1.75} absoluteStrokeWidth aria-hidden />
        </IconButton>
      )}
    </div>
  );
}

// ─── Photo, GIF, video ───────────────────────────────────────────────────────────────────────────────────────

function MediaThumb({ message, content, conversationId, authorName, localUrl, pending }: GroupContentBodyProps & { localUrl?: string; pending: boolean }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const media = content.media!;
  const link = useMediaLink(conversationId, pending ? null : message.finalId ?? message.id, !pending);
  const src = localUrl ?? (link.kind === 'fresh' ? link.url : null);
  const [open, setOpen] = useState(false);
  const box = thumbnailBox(media);
  const isVideo = content.type === 'VIDEO' || (content.type === 'GIF' && media.mimeType === 'video/mp4');
  const label = content.type === 'VIDEO'
    ? t('chat.group.media.videoA11y', { name: authorName, duration: formatDuration(media.durationMs) })
    : t('chat.group.media.photoA11y', { name: authorName });
  return (
    <>
      <Card
        component="button"
        variant="stone"
        size="sm"
        disablePadding
        interactive={src ? 'press' : undefined}
        aria-label={label}
        aria-disabled={!src}
        onClick={() => {
          if (!src) return;
          logPageClickBi({ ...BI, button_name: 'group_media_open' });
          setOpen(true);
        }}
        style={{ position: 'relative', display: 'block', width: box.width, maxWidth: '100%', aspectRatio: `${box.width} / ${box.height}`, overflow: 'hidden', cursor: src ? 'zoom-in' : 'default' }}
      >
        {src && (isVideo ? (
          <video src={src} muted playsInline loop={content.type === 'GIF'} autoPlay={content.type === 'GIF'} preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ))}
        {!src && link.kind === 'unavailable' && (
          <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--ds-text-secondary)', padding: 8 }}>{t('chat.group.media.unavailable')}</span>
        )}
        {content.type === 'VIDEO' && (
          <span aria-hidden style={{ position: 'absolute', insetInlineStart: 8, bottom: 8, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px', borderRadius: 'var(--ds-radius-full)', background: 'var(--sym-bg-elevated)', color: 'var(--ds-text-primary)', fontSize: 12 }}>
            <Play size={12} strokeWidth={1.75} absoluteStrokeWidth aria-hidden />
            {media.durationMs ? formatDuration(media.durationMs) : ''}
          </span>
        )}
        {content.type === 'GIF' && (
          <span aria-hidden style={{ position: 'absolute', insetInlineStart: 8, top: 8, padding: '0 6px', borderRadius: 'var(--ds-radius-full)', background: 'var(--sym-bg-elevated)', color: 'var(--ds-text-primary)', fontSize: 11, fontWeight: 'var(--sym-weight-semibold)' as unknown as number }}>GIF</span>
        )}
      </Card>
      {open && src && (
        <Modal open onClose={() => setOpen(false)} width={960} maxHeightPx={650} title={authorName} scrollBody>
          <div data-testid="group-media-viewer" style={{ display: 'flex', justifyContent: 'center' }}>
            {isVideo ? (
              <video src={src} controls autoPlay playsInline loop={content.type === 'GIF'} style={{ maxWidth: '100%', maxHeight: '75vh' }} />
            ) : (
              <img src={src} alt={label} style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }} />
            )}
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── Document ────────────────────────────────────────────────────────────────────────────────────────────────

function DocumentCard({ message, content, conversationId, pending }: GroupContentBodyProps & { pending: boolean }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const media = content.media!;
  const id = message.finalId ?? message.id;
  const link = useMediaLink(conversationId, pending ? null : id, !pending);
  const name = media.fileName || t('chat.group.media.document');
  const meta = [formatBytes(media.sizeBytes), fileExtension(name)].filter(Boolean).join(' · ');
  const open = async (bi: string) => {
    if (!conversationId || !id) return;
    logPageClickBi({ ...BI, button_name: bi });
    const url = await mediaLinkNow(conversationId, id);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };
  return (
    <Card variant="stone" size="sm" style={{ ...card, flexDirection: 'row', alignItems: 'center' }}>
      <FileText size={28} strokeWidth={1.5} absoluteStrokeWidth aria-hidden style={{ flexShrink: 0, color: 'var(--ds-icon-secondary)' }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div title={name} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 'var(--sym-weight-semibold)' as unknown as number }}>{name}</div>
        <div style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>{link.kind === 'unavailable' ? t('chat.group.media.unavailable') : meta}</div>
      </div>
      {!pending && (
        <>
          <TextButton size="small" onClick={() => void open('group_document_open')}>{t('chat.group.media.open')}</TextButton>
          <IconButton size="small" aria-label={t('chat.group.media.download')} title={t('chat.group.media.download')} onClick={() => void open('group_document_download')}>
            <Download size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
          </IconButton>
        </>
      )}
    </Card>
  );
}

// ─── Voice message ───────────────────────────────────────────────────────────────────────────────────────────

let playing: HTMLAudioElement | null = null;

function VoiceNote({ message, content, conversationId, localUrl, pending }: GroupContentBodyProps & { localUrl?: string; pending: boolean }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const media = content.media!;
  const link = useMediaLink(conversationId, pending ? null : message.finalId ?? message.id, !pending);
  const src = localUrl ?? (link.kind === 'fresh' ? link.url : null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [speed, setSpeed] = useState(1);
  useEffect(() => () => audio.current?.pause(), []);
  // A refreshed link (or the stored file replacing the local preview) moves a paused player onto it.
  useEffect(() => {
    const a = audio.current;
    if (a && src && a.src !== src && a.paused) {
      const at = a.currentTime;
      a.src = src;
      a.currentTime = at;
    }
  }, [src]);
  const total = media.durationMs || (audio.current?.duration ? audio.current.duration * 1000 : 0);
  const fraction = total > 0 ? Math.min(1, elapsed / total) : 0;
  const bars = media.waveform.length ? media.waveform : Array.from({ length: 32 }, () => 30);
  const toggle = () => {
    if (!src) return;
    logPageClickBi({ ...BI, button_name: isPlaying ? 'group_voice_pause' : 'group_voice_play' });
    if (!audio.current) {
      const a = new Audio(src);
      a.ontimeupdate = () => setElapsed(a.currentTime * 1000);
      a.onended = () => {
        setPlaying(false);
        setElapsed(0);
      };
      a.onpause = () => setPlaying(false);
      a.onplay = () => setPlaying(true);
      audio.current = a;
    }
    const a = audio.current;
    a.playbackRate = speed;
    if (a.paused) {
      if (playing && playing !== a) playing.pause();
      playing = a;
      void a.play();
    } else a.pause();
  };
  return (
    <div role="group" aria-label={t('chat.group.voice.a11y', { duration: formatDuration(media.durationMs) })} style={{ ...card, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
      <IconButton size="small" variant="contained" disabled={!src} aria-label={t(isPlaying ? 'chat.group.media.pause' : 'chat.group.media.play')} onClick={toggle}>
        {isPlaying ? <Pause size={16} strokeWidth={1.75} absoluteStrokeWidth aria-hidden /> : <Play size={16} strokeWidth={1.75} absoluteStrokeWidth aria-hidden />}
      </IconButton>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div aria-hidden style={{ display: 'flex', alignItems: 'center', gap: 2, height: 28 }}>
          {bars.map((b, i) => (
            <span key={i} style={{ flex: 1, minWidth: 1, height: `${Math.max(8, b)}%`, borderRadius: 'var(--ds-radius-full)', background: i / bars.length < fraction ? 'var(--accent-solid)' : 'var(--ds-border-strong-default)' }} />
          ))}
        </div>
        <div style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>
          {link.kind === 'unavailable' && !src ? t('chat.group.media.unavailable') : isPlaying || elapsed > 0 ? `${formatDuration(elapsed)} / ${formatDuration(total)}` : formatDuration(media.durationMs)}
        </div>
      </div>
      <Button
        size="small"
        variant="outlined"
        aria-label={t('chat.group.voice.speed', { speed: String(speed) })}
        onClick={() => {
          const next = nextVoiceSpeed(speed);
          setSpeed(next);
          if (audio.current) audio.current.playbackRate = next;
        }}
      >
        {`${speed}×`}
      </Button>
    </div>
  );
}

// ─── Poll ────────────────────────────────────────────────────────────────────────────────────────────────────

/** A voter's or answerer's name, as the who-reacted sheet names them (the viewer is "You"; someone who left, nothing). */
function nameOf(summary: GroupSummary | null, id: string, t: (k: string, v?: Record<string, string | number>) => string): string {
  if (summary?.viewerId === id) return t('chat.group.you');
  const p = personById(summary, id);
  return p ? participantLabel(p, t) : '';
}

function PollCard({ message, content, conversationId, summary, viewerId }: GroupContentBodyProps) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const poll = content.poll!;
  const votes: GroupPollVote[] = message.groupMeta?.pollVotes ?? [];
  const tally = pollTally(poll, votes, viewerId);
  const [showVotes, setShowVotes] = useState(false);
  const stored = !message.groupPost && !!conversationId;
  const tap = (optionId: string) => {
    if (!stored || !viewerId || !conversationId) return;
    logPageClickBi({ ...BI, button_name: 'group_poll_vote' });
    void voteInGroupPoll(conversationId, message, viewerId, nextPollChoice(poll, myPollChoice(votes, viewerId), optionId));
  };
  return (
    <div style={card} data-testid="group-poll">
      <div dir="auto" style={{ fontWeight: 'var(--sym-weight-semibold)' as unknown as number }}>{poll.question}</div>
      <div style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>{t(poll.multipleAnswers ? 'chat.group.poll.selectMany' : 'chat.group.poll.selectOne')}</div>
      <div role={poll.multipleAnswers ? 'group' : 'radiogroup'} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {tally.options.map((o) => (
          <Card
            key={o.id}
            component="button"
            variant="outline"
            size="sm"
            interactive={stored && !!viewerId}
            selected={o.mine}
            role={poll.multipleAnswers ? 'checkbox' : 'radio'}
            aria-checked={o.mine}
            aria-label={t('chat.group.poll.optionA11y', { option: o.text, count: o.count, total: tally.voters })}
            aria-disabled={!stored || !viewerId}
            onClick={() => tap(o.id)}
            style={{ display: 'flex', flexDirection: 'column', gap: 6, textAlign: 'start', width: '100%' }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
              <SelectionIndicator shape={poll.multipleAnswers ? 'checkbox' : 'radio'} checked={o.mine} />
              <span dir="auto" style={{ flex: 1 }}>{o.text}</span>
              <span style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>{o.count}</span>
            </span>
            <ProgressBar progress="neutral" value={Math.round(o.fraction * 100)} decorative style={{ width: '100%' }} />
          </Card>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>{t('chat.group.poll.voters', { count: tally.voters })}</span>
        {tally.voters > 0 && <TextButton size="small" onClick={() => { logPageClickBi({ ...BI, button_name: 'group_poll_view_votes' }); setShowVotes(true); }}>{t('chat.group.poll.viewVotes')}</TextButton>}
      </div>
      {showVotes && (
        <Modal open onClose={() => setShowVotes(false)} title={t('chat.group.poll.votesTitle')} size="minimum" maxHeightPx={650} scrollBody headerSeparator>
          <div data-testid="group-poll-votes" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div dir="auto" style={{ fontWeight: 'var(--sym-weight-semibold)' as unknown as number }}>{poll.question}</div>
            {tally.options.map((o) => (
              <div key={o.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span dir="auto">{o.text}</span><span style={{ color: 'var(--ds-text-secondary)' }}>{o.count}</span></div>
                <ul style={{ listStyle: 'none', margin: '4px 0 0', padding: 0, fontSize: 14, color: 'var(--ds-text-secondary)' }}>
                  {o.voterIds.map((id) => nameOf(summary, id, t)).filter(Boolean).map((name, i) => <li key={i}>{name}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Event ───────────────────────────────────────────────────────────────────────────────────────────────────

const RSVPS: { value: GroupRsvp; key: string; countKey: string }[] = [
  { value: 'GOING', key: 'chat.group.eventCard.going', countKey: 'chat.group.eventCard.goingCount' },
  { value: 'MAYBE', key: 'chat.group.eventCard.maybe', countKey: 'chat.group.eventCard.maybeCount' },
  { value: 'NOT_GOING', key: 'chat.group.eventCard.notGoing', countKey: 'chat.group.eventCard.notGoingCount' },
];

function eventWhen(startIso: string, endIso: string | null, locale: string | undefined): string {
  const start = new Date(startIso);
  const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  if (!endIso) return fmt.format(start);
  const end = new Date(endIso);
  const sameDay = start.toDateString() === end.toDateString();
  return `${fmt.format(start)} – ${sameDay ? new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(end) : fmt.format(end)}`;
}

function EventCard({ message, content, conversationId, summary, viewerId }: GroupContentBodyProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { logPageClickBi } = useGenericBi();
  const event = content.event!;
  const rsvps: GroupEventRsvp[] = message.groupMeta?.eventRsvps ?? [];
  const mine = myRsvp(rsvps, viewerId);
  const counts = rsvpCounts(rsvps);
  const [showResponses, setShowResponses] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const stored = !message.groupPost && !!conversationId;
  return (
    <div style={card} data-testid="group-event">
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <Calendar size={20} strokeWidth={1.5} absoluteStrokeWidth aria-hidden style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ minWidth: 0 }}>
          <div dir="auto" style={{ fontWeight: 'var(--sym-weight-semibold)' as unknown as number }}>{event.title}</div>
          <div style={{ fontSize: 13, color: 'var(--ds-text-secondary)' }}>{eventWhen(event.startDate, event.endDate, locale)}</div>
          {event.location && <div dir="auto" style={{ fontSize: 13, color: 'var(--ds-text-secondary)' }}>{event.location}</div>}
        </div>
      </div>
      {event.description && (
        <span dir="auto" onClick={() => setExpanded((e) => !e)} style={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: expanded ? 'unset' : 3, overflow: 'hidden', whiteSpace: 'pre-wrap', fontSize: 14, cursor: 'pointer' } as React.CSSProperties}>{event.description}</span>
      )}
      <div role="radiogroup" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {RSVPS.map((r) => (
          <Button
            key={r.value}
            size="small"
            variant={mine === r.value ? 'contained' : 'outlined'}
            role="radio"
            aria-checked={mine === r.value}
            disabled={!stored || !viewerId}
            onClick={() => {
              if (!conversationId || !viewerId) return;
              logPageClickBi({ ...BI, button_name: 'group_event_rsvp' });
              void respondToGroupEvent(conversationId, message, viewerId, nextRsvp(mine, r.value));
            }}
          >
            {mine === r.value && <Check size={14} strokeWidth={1.75} absoluteStrokeWidth aria-hidden />}
            {t(r.key)}
          </Button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--ds-text-secondary)' }}>
        {RSVPS.map((r) => <span key={r.value}>{t(r.countKey, { count: counts[r.value] })}</span>)}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {rsvps.length > 0 && <TextButton size="small" onClick={() => { logPageClickBi({ ...BI, button_name: 'group_event_responses' }); setShowResponses(true); }}>{t('chat.group.eventCard.responses')}</TextButton>}
        <TextButton size="small" onClick={() => { logPageClickBi({ ...BI, button_name: 'group_event_add_to_calendar' }); download(`${event.title.slice(0, 40) || 'event'}.ics`, 'text/calendar', eventIcs(event, `${message.finalId ?? message.id}@symphony`)); }}>
          {t('chat.group.eventCard.addToCalendar')}
        </TextButton>
      </div>
      {showResponses && (
        <Modal open onClose={() => setShowResponses(false)} title={t('chat.group.eventCard.responses')} size="minimum" maxHeightPx={650} scrollBody headerSeparator>
          <div data-testid="group-event-responses" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {RSVPS.map((r) => (
              <div key={r.value}>
                <div style={{ fontWeight: 'var(--sym-weight-semibold)' as unknown as number }}>{t(r.countKey, { count: counts[r.value] })}</div>
                <ul style={{ listStyle: 'none', margin: '4px 0 0', padding: 0, fontSize: 14, color: 'var(--ds-text-secondary)' }}>
                  {rsvps.filter((x) => x.response === r.value).map((x) => nameOf(summary, x.metaSiteId, t)).filter(Boolean).map((name, i) => <li key={i}>{name}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Place, contact, sticker ─────────────────────────────────────────────────────────────────────────────────

function LocationCard({ content }: GroupContentBodyProps) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const loc = content.location!;
  return (
    <div style={{ ...card, flexDirection: 'row', alignItems: 'center', gap: 10 }} data-testid="group-location">
      <Card variant="stone" size="sm" aria-hidden style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--accent-solid)' }}>
        <MapPin size={24} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
      </Card>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div dir="auto" style={{ fontWeight: 'var(--sym-weight-semibold)' as unknown as number }}>{loc.label || t('chat.group.location.pinned')}</div>
        {loc.address && <div dir="auto" style={{ fontSize: 13, color: 'var(--ds-text-secondary)' }}>{loc.address}</div>}
        <a href={mapsUrl(loc)} target="_blank" rel="noopener noreferrer" onClick={() => logPageClickBi({ ...BI, button_name: 'group_location_open' })} style={{ fontSize: 13, color: 'var(--accent-solid)' }}>
          {t('chat.group.location.open')}
        </a>
      </div>
    </div>
  );
}

function ContactCard({ content }: GroupContentBodyProps) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const c = content.contact!;
  return (
    <div style={card} data-testid="group-contact">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar initials={avatarInitials(c.name)} size={40} color={avatarSlotFor(c.name)} />
        <div style={{ minWidth: 0 }}>
          <div dir="auto" style={{ fontWeight: 'var(--sym-weight-semibold)' as unknown as number }}>{c.name}</div>
          {c.phones.map((p) => <div key={p}><a href={`tel:${p.replace(/[^\d+]/g, '')}`} style={{ fontSize: 13, color: 'var(--accent-solid)' }}>{p}</a></div>)}
          {c.emails.map((e) => <div key={e}><a href={`mailto:${e}`} style={{ fontSize: 13, color: 'var(--accent-solid)' }}>{e}</a></div>)}
        </div>
      </div>
      <TextButton size="small" onClick={() => { logPageClickBi({ ...BI, button_name: 'group_contact_save' }); download(`${c.name.slice(0, 40) || 'contact'}.vcf`, 'text/vcard', contactVcf(c)); }}>
        {t('chat.group.contact.save')}
      </TextButton>
    </div>
  );
}

/** A sticker in the thread: 140 px, no bubble behind it (WhatsApp's look), as on iOS and Android. */
const STICKER_BUBBLE_PX = 140;

function StickerBody({ content }: GroupContentBodyProps) {
  const { t } = useTranslation();
  const emoji = stickerEmoji(content.stickerId) ?? '';
  return (
    <span role="img" aria-label={t('chat.group.sticker.a11y', { emoji })} data-testid="group-sticker" style={{ display: 'block' }}>
      <StickerArt stickerId={content.stickerId} size={STICKER_BUBBLE_PX} />
    </span>
  );
}
